import React from 'react';
import { AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const RevenueAnalytics = ({ dateRange, onDateRangeChange, selectedRail }) => {
  const dateRangeOptions = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: 'ytd', label: 'Year to Date' }
  ];

  const revenueOverTime = [
    { date: 'Jan 12', merchantFees: 4200, swapCommissions: 2800, otherRevenue: 1200 },
    { date: 'Jan 13', merchantFees: 4800, swapCommissions: 3200, otherRevenue: 1400 },
    { date: 'Jan 14', merchantFees: 5200, swapCommissions: 3600, otherRevenue: 1600 },
    { date: 'Jan 15', merchantFees: 6400, swapCommissions: 4200, otherRevenue: 1800 },
    { date: 'Jan 16', merchantFees: 5800, swapCommissions: 3800, otherRevenue: 1500 },
    { date: 'Jan 17', merchantFees: 6200, swapCommissions: 4400, otherRevenue: 2000 },
    { date: 'Jan 18', merchantFees: 7200, swapCommissions: 4800, otherRevenue: 2200 }
  ];

  const revenueByRail = [
    { name: 'Bitcoin', value: 52340, color: '#F7931A' },
    { name: 'Ethereum', value: 38920, color: '#627EEA' },
    { name: 'Stablecoins', value: 24580, color: '#10B981' },
    { name: 'Merchant', value: 12610, color: '#8B5CF6' }
  ];

  const revenueMetrics = [
    {
      title: 'Total Revenue',
      value: '$128,450',
      change: '+18.2%',
      changeType: 'positive',
      icon: 'DollarSign',
      color: 'var(--color-success)'
    },
    {
      title: 'Merchant Fees',
      value: '$68,240',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'Store',
      color: 'var(--color-primary)'
    },
    {
      title: 'Swap Commissions',
      value: '$42,850',
      change: '+24.8%',
      changeType: 'positive',
      icon: 'RefreshCw',
      color: 'var(--color-accent)'
    },
    {
      title: 'Other Revenue',
      value: '$17,360',
      change: '+8.3%',
      changeType: 'positive',
      icon: 'TrendingUp',
      color: 'var(--color-secondary)'
    }
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg shadow-lg p-3">
          <p className="text-sm font-semibold text-foreground mb-2">{payload?.[0]?.payload?.date}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 mb-1">
              <span className="text-xs text-muted-foreground">{entry?.name}:</span>
              <span className="text-xs font-semibold" style={{ color: entry?.color }}>
                ${entry?.value?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Revenue Analytics</h3>
        <div className="w-48">
          <Select
            options={dateRangeOptions}
            value={dateRange}
            onChange={onDateRangeChange}
          />
        </div>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {revenueMetrics?.map((metric, index) => (
          <div key={index} className="card">
            <div className="flex items-start justify-between mb-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${metric?.color}20` }}
              >
                <Icon name={metric?.icon} size={20} color={metric?.color} />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-1">{metric?.title}</p>
            <h3 className="text-2xl font-bold text-foreground font-data mb-2">{metric?.value}</h3>
            <div className="flex items-center gap-1">
              <Icon 
                name={metric?.changeType === 'positive' ? 'TrendingUp' : 'TrendingDown'} 
                size={14} 
                className={metric?.changeType === 'positive' ? 'text-success' : 'text-error'}
              />
              <span className={`text-xs font-semibold ${metric?.changeType === 'positive' ? 'text-success' : 'text-error'}`}>
                {metric?.change}
              </span>
              <span className="text-xs text-muted-foreground">vs last period</span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Over Time */}
        <div className="lg:col-span-2 card">
          <div className="mb-4">
            <h4 className="text-base font-semibold text-foreground mb-1">Revenue Over Time</h4>
            <p className="text-sm text-muted-foreground">Daily revenue breakdown by source</p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={revenueOverTime}>
              <defs>
                <linearGradient id="merchantGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="swapGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="otherGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} />
              <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} />
              <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Area 
                type="monotone" 
                dataKey="merchantFees" 
                name="Merchant Fees"
                stroke="#8B5CF6" 
                fill="url(#merchantGradient)" 
                strokeWidth={2}
              />
              <Area 
                type="monotone" 
                dataKey="swapCommissions" 
                name="Swap Commissions"
                stroke="#10B981" 
                fill="url(#swapGradient)" 
                strokeWidth={2}
              />
              <Area 
                type="monotone" 
                dataKey="otherRevenue" 
                name="Other Revenue"
                stroke="#F59E0B" 
                fill="url(#otherGradient)" 
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue by Payment Rail */}
        <div className="card">
          <div className="mb-4">
            <h4 className="text-base font-semibold text-foreground mb-1">Revenue by Rail</h4>
            <p className="text-sm text-muted-foreground">Distribution across payment methods</p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={revenueByRail}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {revenueByRail?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value) => `$${value?.toLocaleString()}`}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {revenueByRail?.map((rail, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: rail?.color }} />
                  <span className="text-sm text-foreground">{rail?.name}</span>
                </div>
                <span className="text-sm font-semibold text-foreground font-data">
                  ${rail?.value?.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueAnalytics;